import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Zap, Lock, Mail, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Access granted");
      navigate("/admin");
    } catch (err) {
      const msg = apiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="relative rounded-2xl border border-border bg-card/80 backdrop-blur-xl p-8 scanline overflow-hidden">
          <div className="absolute top-0 left-0 h-0.5 w-full bg-gradient-to-r from-primary via-accent to-transparent" />
          <Link to="/" className="flex items-center gap-2 font-mono-head font-extrabold mb-8">
            <span className="grid place-items-center h-8 w-8 rounded bg-primary text-primary-foreground glow-primary">
              <Zap size={18} />
            </span>
            FENIX<span className="text-primary">·</span>ZERO
          </Link>

          <h1 className="font-mono-head text-2xl font-bold">Command Console</h1>
          <p className="text-sm text-muted-foreground mt-1 mb-6 font-code">
            // authenticate to access routing engine
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-mono-head uppercase tracking-widest text-muted-foreground">
                Operator Email
              </label>
              <div className="mt-1.5 relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="info@joe-fenix-method.com"
                  className="w-full bg-background border border-input rounded-md pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-mono-head uppercase tracking-widest text-muted-foreground">
                Access Key
              </label>
              <div className="mt-1.5 relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="login-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••"
                  className="w-full bg-background border border-input rounded-md pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                />
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2 font-code"
              >
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-mono-head font-semibold py-2.5 rounded-md hover:bg-[#E04800] transition-colors disabled:opacity-60 glow-primary"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : "AUTHENTICATE"}
            </button>
          </form>
        </div>
        <p className="text-center text-xs text-muted-foreground mt-4 font-code">
          Zero third-party tracking · encrypted session cookie
        </p>
      </motion.div>
    </div>
  );
}
